import { Box, CircularProgress, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useContext, useState } from "react";
import { AccountCardWrapper } from "../../components/AccountCardWrapper";
import { SetupPageContainer } from "../../../utils/commonStyledComponents/commonStyled";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import { adminApiService } from "../../../utils/api";
import { messages } from "../../../locales/messages";
import { StyledButton } from "./styled";
import { toastError, toastSuccess } from "../../../utils/pageUtil/toast";
import { useAuthHeaders } from "../../../hooks/useAuthHeaders";
import { GoFlyerAdminContext } from "../../../contextApi/context";
import { SUPERADMIN } from "../../../utils/constant/stringConstant";
import { EngagementIngestPanel } from "./engagement/EngagementIngestPanel";

const ElasticTrigger = () => {
  const { t } = useTranslation();
  const auth = useAuthHeaders();
  const { state } = useContext(GoFlyerAdminContext);
  const [loading, setLoading] = useState<Record<string, boolean>>({});

  // Every /admin-engagement route requires a SuperAdmin JWT, so the panel would
  // only ever 403 for anyone else who holds the ElasticTrigger-view permission.
  const isSuperAdmin = state.user?.parsedRoles?.name === SUPERADMIN;

  const handleTrigger = async (
    condition:
      | "promotion"
      | "flyer"
      | "image"
      | "store"
      | "merchant"
      | "menu"
      | "flyer-item"
      | "flyeritemlist"
  ) => {
    try {
      setLoading((prev) => ({ ...prev, [condition]: true }));
      toastSuccess(`${t(messages.INITIALIZING_INDEXING())} ${condition}`);
      await adminApiService.searchAdminControllerTriggerElasticSearch(
        condition
      );
      toastSuccess(`${t(messages.INDEXING_SUCCESS())} ${condition}`);
    } catch (error) {
      toastError(`${t(messages.INDEXING_FAILED())} ${condition}`);
    } finally {
      setLoading((prev) => ({ ...prev, [condition]: false }));
    }
  };

  const handleSyncAiItems = async () => {
    try {
      setLoading((prev) => ({ ...prev, "ai-items": true }));
      toastSuccess(`${t(messages.INITIALIZING_INDEXING())} AI Generated Items`);
      await adminApiService.gfFlyerItemAdminControllerSyncAiGeneratedItemsToElasticsearch(
        auth
      );
      toastSuccess(`${t(messages.INDEXING_SUCCESS())} AI Generated Items`);
    } catch (error) {
      toastError(`${t(messages.INDEXING_FAILED())} AI Generated Items`);
    } finally {
      setLoading((prev) => ({ ...prev, "ai-items": false }));
    }
  };

  const handleEnqueueImageEmbeddingsNonExpiredLists = async () => {
    try {
      setLoading((prev) => ({ ...prev, "image-embeddings-non-expired": true }));
      toastSuccess(
        `${t(messages.INITIALIZING_INDEXING())} ${t(messages.ENQUEUE_CLIP_IMAGE_EMBEDDINGS_NON_EXPIRED_LISTS())}`
      );
      await adminApiService.gfFlyerItemAdminControllerEnqueueImageEmbeddingsForNonExpiredLists(
        auth
      );
      toastSuccess(
        `${t(messages.INDEXING_SUCCESS())} ${t(messages.ENQUEUE_CLIP_IMAGE_EMBEDDINGS_NON_EXPIRED_LISTS())}`
      );
    } catch (error) {
      toastError(
        `${t(messages.INDEXING_FAILED())} ${t(messages.ENQUEUE_CLIP_IMAGE_EMBEDDINGS_NON_EXPIRED_LISTS())}`
      );
    } finally {
      setLoading((prev) => ({
        ...prev,
        "image-embeddings-non-expired": false,
      }));
    }
  };

  const renderButton = (
    condition:
      | "promotion"
      | "flyer"
      | "store"
      | "merchant"
      | "menu"
      | "flyer-item"
      | "flyeritemlist",
    label: string
  ) => (
    <StyledButton
      endIcon={
        loading[condition] ? (
          <CircularProgress size={20} color="inherit" />
        ) : (
          <AutorenewIcon />
        )
      }
      onClick={() => handleTrigger(condition)}
      disabled={loading[condition]}
    >
      {label}
    </StyledButton>
  );

  return (
    <SetupPageContainer>
      <AccountCardWrapper title={`${t(messages.ELASTIC_TRIGGER())}`}>
        <Box
          sx={{
            justifyContent: "center",
            alignItems: "center",
            display: "flex",
            flexDirection: "column",
            rowGap: "20px",
            marginTop: "20px",
            marginBottom: "20px",
          }}
        >
          {renderButton("flyer", t(messages.START_INDEXING_FLYERS()))}
          {renderButton("promotion", t(messages.START_INDEXING_PROMOTIONS()))}
          {renderButton("store", t(messages.START_INDEXING_STORES()))}
          {renderButton("merchant", t(messages.START_INDEXING_MERCHANTS()))}
          {renderButton("menu", t(messages.START_INDEXING_MENUS()))}
          {renderButton(
            "flyer-item",
            t(messages.START_INDEXING_FLYERS_ITEMS())
          )}
          {renderButton(
            "flyeritemlist",
            t(messages.START_INDEXING_FLYERS_ITEMS_LIST())
          )}
          <Tooltip
            title={t(messages.SYNC_AI_GENERATED_FLYER_ITEMS_TOOLTIP())}
            placement="top"
            arrow
          >
            <span>
              <StyledButton
                endIcon={
                  loading["ai-items"] ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <AutorenewIcon />
                  )
                }
                onClick={handleSyncAiItems}
                disabled={loading["ai-items"]}
              >
                {t(messages.SYNC_AI_GENERATED_FLYER_ITEMS())}
              </StyledButton>
            </span>
          </Tooltip>
          <Tooltip
            title={t(
              messages.ENQUEUE_CLIP_IMAGE_EMBEDDINGS_NON_EXPIRED_LISTS_TOOLTIP()
            )}
            placement="top"
            arrow
          >
            <span>
              <StyledButton
                endIcon={
                  loading["image-embeddings-non-expired"] ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <AutorenewIcon />
                  )
                }
                onClick={handleEnqueueImageEmbeddingsNonExpiredLists}
                disabled={loading["image-embeddings-non-expired"]}
              >
                {t(messages.ENQUEUE_CLIP_IMAGE_EMBEDDINGS_NON_EXPIRED_LISTS())}
              </StyledButton>
            </span>
          </Tooltip>
        </Box>
      </AccountCardWrapper>

      {isSuperAdmin && (
        // CardBox carries no outer margin, so stacked cards need the gap here.
        <Box sx={{ width: "100%", mt: 3, mb: 3 }}>
          <EngagementIngestPanel />
        </Box>
      )}
    </SetupPageContainer>
  );
};

export default ElasticTrigger;
